import { TActivity } from '@/domain/Activities.domain';
import { SEARCH_IMAGE_SIZES, SEARCH_MAIN_IMAGE_SIZE } from '@/service/constants/variables';
import { createImagesSet } from '@/service/creators/createImageUrl';
import { numberWithCommaFormatter } from '@/service/utils/numberWithCommaFormatter';
import { LocationIcon } from '@/ui/common/Icons/Loaction';
import { SliderPicture } from '@/ui/common/Slider/core/SliderPicture';
import { RatingStars } from './RatingStars';
import { DurationIcon } from '@/ui/common/Icons/Duration';
import { Arrow } from '@/ui/common/Arrows/ArrowDropdown';
import { useState } from 'react';
import { cn } from '@/service/utils/cn';
import { TDayItem } from '@/domain/Availability.domain';
import { SearchSchedules } from './SearchSchedules';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import Link from 'next/link';
import { TImageSlug } from '@/domain/Images.domain';
import { Slider } from '@/ui/common/Slider/Slider';
import { isPastTime } from '@/service/utils/isPastTime';
import { scrollToId } from '@/service/utils/scrollToId';
import { ArrowRightIcon } from '@/ui/common/Icons/ArrowRightIcon';

type TSearchCardProps = {
    activity: TActivity;
    address: string;
    showReviews: boolean;
    pathname: string;
    dateQuery: string;
    partnerPhone: string;
    selectSchedule: (id: number) => void;
    images?: TImageSlug[];
    isLoading?: boolean;
    schedules?: TDayItem[];
    price?: number;
};

export const SearchCard = ({
    activity,
    address,
    showReviews,
    partnerPhone,
    selectSchedule,
    schedules,
    isLoading,
    price,
    pathname,
    dateQuery,
    images,
}: TSearchCardProps) => {
    const [openDetails, setOpenDetails] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const { timezone } = usePartnerStore();

    const [image] = createImagesSet([{ id: activity.id }], SEARCH_MAIN_IMAGE_SIZE);
    const reviewsCount = +activity.rate_count;
    const reviewsCountWithComma = numberWithCommaFormatter(reviewsCount);
    const status = activity.statuses?.general?.[0];
    const slides =
        openDetails &&
        createImagesSet(
            [{ id: activity.id }, ...(images?.length ? images : [])],
            SEARCH_IMAGE_SIZES
        );

    const list = schedules?.[0]?.schedules?.length
        ? schedules[0].schedules.filter(
              ({ is_avail, call_to_booking, time }) =>
                  is_avail || (call_to_booking && !isPastTime(time, timezone))
          )
        : undefined;

    if (!isLoading && !list?.length) {
        return <></>;
    }

    return (
        <div className="shadow-main relative mb-3 w-full overflow-hidden rounded-2xl bg-white p-3 text-gray-1 dark:bg-dark-3 dark:text-white lg:min-h-[184px]">
            <div className="lg:flex">
                <SliderPicture
                    className="h-auto overflow-hidden rounded-2xl lg:h-[130px] lg:w-[130px] lg:min-w-[130px]"
                    srcSet={image.srcSet}
                    slug={image.slug}
                />
                <div className="w-full md:min-h-[152px]">
                    <div className="py-3 lg:p-0 lg:pl-3">
                        <h2
                            data-test-id="card-title"
                            className="tx-large-semibold flex justify-between"
                        >
                            <Link
                                href={`${pathname}/details/${activity.id + dateQuery}`}
                                className="hover:underline"
                            >
                                {activity.name}
                            </Link>
                            {!!price && (
                                <div className="absolute right-5 top-5 ml-1 flex h-fit min-w-[84px] items-center justify-center rounded-2xl bg-brand px-4 py-0.5 text-white lg:static lg:min-w-[100px]">
                                    <span className="tx-normal-medium">from&nbsp;</span>
                                    {getFormattedPrice(price)}
                                </div>
                            )}
                        </h2>
                        {!!address && (
                            <p className="tx-medium-regular flex text-gray-2 dark:text-gray-3">
                                <LocationIcon className="[&>*]:fill-gray-2 [&>*]:dark:fill-gray-3" />
                                <span className="mt-0.5">{address}</span>
                            </p>
                        )}
                        <div className="tx-medium-regular flex text-gray-2 dark:text-gray-3">
                            {showReviews && !!reviewsCount && (
                                <div className="flex items-center" data-test-id="card-reviews">
                                    <RatingStars
                                        rating={activity.rate}
                                        data-test-id="Rating_start"
                                        count={activity.rate_count}
                                    />
                                    <span className="mx-2">
                                        {`${reviewsCountWithComma} ${
                                            reviewsCount > 1 || reviewsCount < 1
                                                ? 'ratings'
                                                : 'rating'
                                        }`}
                                    </span>
                                    {!!activity.duration && (
                                        <span
                                            className="flex items-center whitespace-nowrap"
                                            data-test-id="card-duration"
                                        >
                                            <DurationIcon className="[&>*]:fill-gray-2 [&>*]:dark:fill-gray-3" />
                                            {activity.duration}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <SearchSchedules
                            isLoading={isLoading}
                            list={list}
                            showMore={showMore}
                            selectSchedule={selectSchedule}
                            partnerPhone={partnerPhone}
                        />
                        {
                            <div
                                className={cn(
                                    'grid grid-cols-2 gap-1.5 lg:grid-cols-1',
                                    list?.length && list.length <= 4 && 'grid-cols-1'
                                )}
                            >
                                <div
                                    className="tx-medium-regular left-3 top-[148px] flex h-8
                w-full cursor-pointer items-center justify-center rounded-2xl bg-gradient-to-r from-gray-8 to-gray-8 text-center text-gray-2 hover:opacity-90 dark:from-gray-10  dark:to-gray-9 dark:text-white lg:absolute lg:h-6 lg:w-[130px]"
                                    onClick={() => setOpenDetails((prev) => !prev)}
                                >
                                    {`${openDetails ? 'Hide' : 'Show'} details`}
                                    <Arrow
                                        toTop={openDetails}
                                        className="duration-400 ml-2 inline-block h-3 w-3 lg:ml-1"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowMore((prev) => {
                                            if (prev) {
                                                setTimeout(
                                                    () =>
                                                        scrollToId(
                                                            'schedules-list' +
                                                                list?.[0]?.schedule_id
                                                        ),
                                                    50
                                                );
                                                return false;
                                            } else {
                                                return true;
                                            }
                                        })
                                    }
                                    className={cn(
                                        'tx-medium-medium block h-8 w-full cursor-pointer rounded-2xl bg-gradient-to-r from-blue-2 to-blue-3 text-center text-white shadow-lightButton hover:opacity-90 lg:h-6',
                                        list?.length && list.length <= 6 && 'lg:hidden',
                                        list?.length && list.length <= 4 && 'hidden',
                                        isLoading &&
                                            'animate-pulse bg-gray-4 from-current to-current text-gray-4 shadow-none dark:bg-blue-main dark:text-blue-main'
                                    )}
                                >
                                    {showMore ? 'Show less options' : 'View more options'}
                                    <ArrowRightIcon
                                        className={cn(
                                            'duration-400 ml-2 inline-block h-2.5 w-2.5 md:ml-1 lg:ml-2 [&>*]:fill-white',
                                            showMore ? '-rotate-90' : 'rotate-90',
                                            isLoading &&
                                                'animate-pulse [&>*]:fill-gray-4 dark:[&>*]:fill-blue-main'
                                        )}
                                    />
                                </button>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className={cn('overflow-hidden duration-300', openDetails ? 'h-auto' : 'h-0')}>
                {!!slides && (
                    <div className="relative min-h-[156px] w-full bg-gradient-image md:mt-3">
                        <Slider slides={slides} className="h-auto w-full flex-shrink" />
                    </div>
                )}
                {!!status && (
                    <div
                        data-test-id="card-status"
                        className="tx-large-semibold mt-2 text-gray-1 dark:text-white"
                    >
                        {status}
                    </div>
                )}
                <div
                    className={`tx-medium-regular my-2 break-words text-gray-2 dark:text-gray-4`}
                    data-test-id="card-description"
                >
                    {activity.description_clean}
                </div>
            </div>
        </div>
    );
};
